"use client";

import { useMemo, useState } from "react";

import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Minus, Plus, X } from "lucide-react";
import { useTranslations } from "next-intl";

import { MintButton } from "@/components/shared/mint-button";
import { SearchField } from "@/components/shared/search-field";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useMediaQuery } from "@/hooks/use-media-query";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

import { useTimezones } from "../hooks/use-timezones";
import { useWidgetParams } from "../hooks/use-widget-params";

import type { ClocksPayload, TimezoneOption } from "@/types";

/**
 * Choose and order the cities on the world-clock card.
 *
 * The same sheet the dashboard and the bottom nav are customized with — right-
 * side slide-over on desktop, full-screen from the bottom on mobile, draft state
 * that only Apply commits, dnd-kit sorting, Reset and Apply in a fixed footer.
 * A user who has reordered their widgets already knows how to work this.
 *
 * Two lists rather than one, because the two halves are different sizes and
 * different jobs. The chosen cities are few and ORDERED — the order is the whole
 * point, since it decides which four the card shows — so they get the grip
 * handles. Every other city is a flat catalogue of four hundred entries you find
 * by typing, where a grip handle would be meaningless.
 */
export function ClockSettingsSheet({ payload }: { payload: ClocksPayload }) {
  const t = useTranslations("Dashboard");
  const tPage = useTranslations("Page");
  const [open, setOpen] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  const { params, setParams, isSaving } = useWidgetParams("world_clocks");
  // Fetched only once the sheet has been opened — see useTimezones.
  const { data: cities, isPending } = useTimezones(open);

  // What is currently SAVED. Falls back to what the card is showing, which
  // differs exactly once: before anyone has chosen anything, when the server
  // defaulted to the account's own timezone. Seeding from the payload means the
  // first city someone adds joins that one instead of silently replacing it.
  const saved = useMemo<string[]>(() => {
    const stored = params?.cities;

    return Array.isArray(stored) && stored.length > 0
      ? stored.filter((value): value is string => typeof value === "string")
      : payload.items.map((item) => item.timezone);
  }, [params?.cities, payload.items]);

  // The edit in progress, re-seeded in the OPEN handler rather than an effect:
  // opening is an event, so it is handled as one, and an effect would both cost
  // a second render and have to lie about its dependencies (reacting to `saved`
  // would wipe the draft whenever anything refreshed the catalog).
  const [draft, setDraft] = useState<string[]>(saved);
  const [query, setQuery] = useState("");

  const byValue = useMemo(
    () => new Map((cities ?? []).map((city) => [city.value, city])),
    [cities],
  );

  // Full means "can't KEEP another", which is a much higher bar than "won't be
  // drawn" — the cities past max_clocks are the reserve, not rejects.
  const isFull = draft.length >= payload.max_cities;

  const isDirty =
    draft.length !== saved.length ||
    draft.some((timezone, index) => timezone !== saved[index]);

  // Everything not already chosen, narrowed by the search box. Matched on city
  // AND country, so "germany" finds Berlin and nobody has to know a city sits
  // under "Europe/".
  const results = useMemo(() => {
    const needle = query.trim().toLowerCase();

    return (cities ?? []).filter((city) => {
      if (draft.includes(city.value)) return false;
      if (!needle) return true;

      return (
        city.label.toLowerCase().includes(needle) ||
        (city.region?.toLowerCase().includes(needle) ?? false)
      );
    });
  }, [cities, draft, query]);

  function handleOpenChange(next: boolean) {
    if (next) {
      setDraft(saved);
      setQuery("");
    }
    setOpen(next);
  }

  function add(timezone: string) {
    // Appended, never inserted: a new city joins the end of the order, so adding
    // one can't silently push another off the card.
    setDraft((current) =>
      current.includes(timezone) || current.length >= payload.max_cities
        ? current
        : [...current, timezone],
    );
  }

  function remove(timezone: string) {
    setDraft((current) => current.filter((value) => value !== timezone));
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = draft.indexOf(String(active.id));
    const newIndex = draft.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    setDraft(arrayMove(draft, oldIndex, newIndex));
  }

  async function handleApply() {
    try {
      await setParams({ cities: draft });
      setOpen(false);
    } catch (error) {
      // Left open, with the draft intact, so the edit isn't lost to a failed
      // request. The app's error toast has already said it failed.
      logger.warn("[dashboard] could not save clock cities", error);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => handleOpenChange(true)}
        aria-label={t("clocksSettings")}
        title={t("clocksSettings")}
        // The DS recessed chip, same as WidgetOpenLink — a card's controls all
        // look alike, whether they open something or configure it.
        className="flex size-8 shrink-0 items-center justify-center rounded-full bg-surface-input text-fg-muted shadow-[var(--inner-button-shadow)] transition-transform hover:text-foreground active:scale-95"
      >
        <Plus className="size-4" />
      </button>

      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent
          side={isDesktop ? "right" : "bottom"}
          showCloseButton={false}
          className={cn(
            "flex flex-col gap-0 bg-surface-main p-0",
            isDesktop
              ? "h-full w-1/2 max-w-none sm:max-w-none"
              : "inset-0 top-0 h-dvh w-full rounded-none",
          )}
          // Don't auto-focus the first grip handle — it would paint a focus ring
          // and make one row look pre-selected.
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <SheetHeader className="shrink-0 gap-1 px-6 pb-2 pt-[calc(env(safe-area-inset-top)+1.5rem)]">
            <div className="flex items-center justify-between">
              <SheetTitle className="font-display text-2xl font-normal text-foreground">
                {t("clocksSettings")}
              </SheetTitle>
              <SheetClose asChild>
                <button
                  type="button"
                  className="flex size-10 items-center justify-center rounded-full bg-surface-tab-inactive text-fg-muted transition-colors hover:bg-surface-tab-inactive/70"
                  aria-label={tPage("close")}
                >
                  <X className="size-5" />
                </button>
              </SheetClose>
            </div>
            <SheetDescription>{t("clocksHint")}</SheetDescription>
          </SheetHeader>

          <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto overscroll-contain px-6 py-4">
            <section className="flex flex-col gap-2">
              <div className="flex items-baseline justify-between gap-2">
                <h4 className="text-xs font-semibold tracking-wider text-fg-muted uppercase">
                  {t("clocksYours")}
                </h4>
                <span
                  className={cn(
                    "text-xs tabular-nums",
                    isFull ? "text-tx-orange" : "text-fg-muted",
                  )}
                >
                  {t("slotCount", {
                    count: draft.length,
                    limit: payload.max_cities,
                  })}
                </span>
              </div>

              {/* Said once, up front, rather than left to be inferred from the
                  greyed-out rows below — the rule that the ORDER decides which
                  cities appear is the whole reason this list can be dragged. */}
              {draft.length > payload.max_clocks && (
                <p className="text-xs text-fg-muted">
                  {t("clocksFirstShown", { count: payload.max_clocks })}
                </p>
              )}

              {draft.length === 0 ? (
                <p className="rounded-2xl bg-surface-tab-inactive/15 px-4 py-6 text-center text-sm text-fg-muted">
                  {t("clocksNoneChosen")}
                </p>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={draft}
                    strategy={verticalListSortingStrategy}
                  >
                    <ul className="flex flex-col gap-2">
                      {draft.map((timezone, index) => (
                        <ChosenCity
                          key={timezone}
                          timezone={timezone}
                          city={byValue.get(timezone)}
                          payload={payload}
                          isShown={index < payload.max_clocks}
                          notShownLabel={t("clocksNotShown")}
                          onRemove={() => remove(timezone)}
                          reorderLabel={t("customizeReorder", {
                            label: labelFor(timezone, payload, byValue),
                          })}
                          removeLabel={t("clocksRemove", {
                            label: labelFor(timezone, payload, byValue),
                          })}
                        />
                      ))}
                    </ul>
                  </SortableContext>
                </DndContext>
              )}
            </section>

            <section className="flex min-h-0 flex-col gap-2">
              <h4 className="text-xs font-semibold tracking-wider text-fg-muted uppercase">
                {t("clocksAll")}
              </h4>

              <SearchField
                value={query}
                onChange={setQuery}
                placeholder={t("clocksSearch")}
              />

              {isPending ? (
                <p className="py-6 text-center text-sm text-fg-muted">
                  {t("clocksLoading")}
                </p>
              ) : results.length === 0 ? (
                <p className="py-6 text-center text-sm text-fg-muted">
                  {t("clocksNoCity")}
                </p>
              ) : (
                // Every match, not a truncated head: this is the "big list" —
                // scrolling four hundred cities is a legitimate way to browse,
                // and a silent cut-off is worse than a long scroll because it
                // reads as "your city isn't supported".
                <ul className="flex flex-col gap-2 pb-2">
                  {results.map((city) => (
                    <CatalogCity
                      key={city.value}
                      city={city}
                      disabled={isFull}
                      onAdd={() => add(city.value)}
                      addLabel={t("clocksAdd", { label: city.label })}
                    />
                  ))}
                </ul>
              )}
            </section>
          </div>

          <div className="flex shrink-0 items-center gap-3 border-t border-border-soft/30 px-6 py-5 pb-[calc(env(safe-area-inset-bottom)+1.25rem)]">
            <Button
              type="button"
              variant="outline"
              // Back to nothing chosen, which is what the server reads as "use
              // this account's own timezone" — the state a new user starts in.
              onClick={() => setDraft([])}
              disabled={isSaving}
              className="h-[50px] flex-1 rounded-full border-border-soft text-base font-normal"
            >
              <X className="mr-2 size-4" />
              {t("customizeReset")}
            </Button>
            <MintButton
              type="button"
              onClick={handleApply}
              // Nothing changed means nothing to write, and an Apply that fires a
              // request to save the same list is worse than one that's off.
              disabled={isSaving || !isDirty}
              className="h-[50px] flex-1 text-base"
            >
              {tPage("apply")}
            </MintButton>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

/** One chosen city: draggable, and removable with the switch. */
function ChosenCity({
  timezone,
  city,
  payload,
  isShown,
  notShownLabel,
  onRemove,
  reorderLabel,
  removeLabel,
}: {
  timezone: string;
  city: TimezoneOption | undefined;
  payload: ClocksPayload;
  /** Within the first max_clocks, so it actually appears on the card. */
  isShown: boolean;
  notShownLabel: string;
  onRemove: () => void;
  reorderLabel: string;
  removeLabel: string;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: timezone });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
  };

  // The card's own copy of this clock, when it has one — the only place an
  // offset is available, since the catalog deliberately carries none.
  const clock = payload.items.find((item) => item.timezone === timezone);

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        "group flex items-center gap-2.5 rounded-2xl border px-2 py-2 transition-colors",
        // Same treatment the widget customizer gives a hidden row, and for the
        // same reason: this city is kept but not currently on show.
        isShown
          ? "border-border/40 bg-surface-tab-inactive/30"
          : "border-transparent bg-surface-tab-inactive/15",
        isDragging && "border-border shadow-lg",
      )}
    >
      {/* Only the handle starts a drag (touch-none), so the list still scrolls. */}
      <button
        type="button"
        aria-label={reorderLabel}
        className="flex size-9 shrink-0 cursor-grab touch-none items-center justify-center rounded-lg text-muted-foreground/50 outline-none transition-colors hover:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring/50 active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-5" />
      </button>

      <div className="flex min-w-0 flex-1 flex-col">
        <span
          className={cn(
            "truncate text-sm font-medium transition-colors",
            isShown ? "text-foreground" : "text-muted-foreground/70",
          )}
        >
          {city?.label ?? clock?.city ?? fallbackLabel(timezone)}
        </span>
        <span className="truncate text-xs text-fg-muted">
          {/* Says "not shown" INSTEAD of the offset for a reserve city, because
              the card isn't showing it and there is no resolved offset to
              print — the payload only carries the ones it drew. */}
          {isShown
            ? [city?.region ?? clock?.region, clock?.utc_offset_label]
                .filter(Boolean)
                .join(" · ")
            : notShownLabel}
        </span>
      </div>

      {/* Minus, mirroring the plus on the catalogue rows below. A switch was
          wrong here: it implies an on/off state the thing can rest in, and a city
          has none — unchosen cities live in the list below, so "off" is not a
          state, it is a move between the two lists. Plus sends a city up, minus
          sends it back down. */}
      <button
        type="button"
        onClick={onRemove}
        aria-label={removeLabel}
        title={removeLabel}
        className="flex size-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground/50 transition-colors hover:text-foreground"
      >
        <Minus className="size-5" />
      </button>
    </li>
  );
}

/** One city in the big catalogue. The whole row adds it. */
function CatalogCity({
  city,
  disabled,
  onAdd,
  addLabel,
}: {
  city: TimezoneOption;
  disabled: boolean;
  onAdd: () => void;
  addLabel: string;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onAdd}
        disabled={disabled}
        aria-label={addLabel}
        // The whole row, not a small target on the end of it: this list is long
        // and is used on a phone. Same shell as the rows above so the two read
        // as one system, minus the grip — order means nothing here.
        className={cn(
          "flex w-full items-center gap-2.5 rounded-2xl border border-transparent bg-surface-tab-inactive/15 py-2 pr-2 pl-4 text-left transition-colors",
          disabled
            ? "cursor-not-allowed opacity-40"
            : "hover:border-border/40 hover:bg-surface-tab-inactive/30",
        )}
      >
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-medium text-foreground">
            {city.label}
          </span>
          {city.region && (
            <span className="truncate text-xs text-fg-muted">{city.region}</span>
          )}
        </div>

        {/* Always a plus, never a tick. A tick here used to mark the at-the-limit
            rows, which read as "already added" — the opposite of what it meant.
            These rows are BY DEFINITION not chosen (chosen cities are filtered out
            into the list above), so the only honest icon is the one that says what
            the row does; the row's own dimming says it can't right now. */}
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground/50">
          <Plus className="size-5" />
        </span>
      </button>
    </li>
  );
}

function labelFor(
  timezone: string,
  payload: ClocksPayload,
  byValue: Map<string, TimezoneOption>,
): string {
  return (
    byValue.get(timezone)?.label ??
    payload.items.find((item) => item.timezone === timezone)?.city ??
    fallbackLabel(timezone)
  );
}

/** For the moment before the catalogue lands on a city the card isn't showing. */
function fallbackLabel(timezone: string): string {
  return timezone.split("/").pop()?.replace(/_/g, " ") ?? timezone;
}
